import { Link } from "react-router-dom";
import { LogoMark } from "@/components/app/Brand";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Purpose and scope",
    body: "This Data Processing Agreement (\"DPA\") forms part of the agreement between CombinedWork (\"Processor\") and the customer (\"Controller\") and applies whenever CombinedWork processes personal data on the Controller's behalf in connection with the Service — including data read from the Controller's connected HubSpot and Xero accounts.",
  },
  {
    title: "2. Roles",
    body: "The Controller determines the purposes and means of processing personal data (its clients, deals, contacts, invoices and team members). CombinedWork acts as Processor, and only processes that data to provide, secure and support the Service, on the Controller's documented instructions.",
  },
  {
    title: "3. Subject matter and duration",
    body: "Processing covers the categories of personal data the Controller stores in or connects to the Service — team member and client contact details, deal and invoice records, and project communications — for the duration of the subscription, plus any post-termination retention period set out in the Privacy Policy.",
  },
  {
    title: "4. Sub-processors",
    body: "CombinedWork uses vetted sub-processors for hosting, database, email delivery and payment processing, each bound by a data processing agreement no less protective than this one. An up-to-date sub-processor list is available on request to hello@combinedwork.com; we'll give reasonable notice before adding a new sub-processor so the Controller can object.",
  },
  {
    title: "5. Security measures",
    body: "CombinedWork maintains technical and organisational measures appropriate to the risk, including encryption in transit (TLS) and at rest for OAuth tokens and credentials, role-based access control, rate limiting on authentication endpoints, and webhook signature verification on all third-party integrations (HubSpot, Xero).",
  },
  {
    title: "6. Sub-processing of connected integrations",
    body: "Where the Controller connects HubSpot, Xero, or another third-party account, CombinedWork processes only the data required to power the features the Controller enables (e.g. deal pipeline for margin tracking, invoice actuals for reconciliation), and stores the resulting OAuth credentials encrypted at rest.",
  },
  {
    title: "7. Data subject requests",
    body: "CombinedWork will assist the Controller, insofar as reasonably possible, in responding to data subject access, correction, deletion and portability requests concerning data processed on the Controller's behalf, using the tools available in the Service or by direct request to hello@combinedwork.com.",
  },
  {
    title: "8. Breach notification",
    body: "CombinedWork will notify the Controller without undue delay, and in any case within 72 hours of becoming aware, of any confirmed personal data breach affecting the Controller's data, together with the information reasonably available to help the Controller meet its own notification obligations.",
  },
  {
    title: "9. International transfers",
    body: "Where personal data is transferred outside the UK/EEA, CombinedWork relies on appropriate safeguards — such as the UK International Data Transfer Addendum or the EU Standard Contractual Clauses — with the receiving sub-processor.",
  },
  {
    title: "10. Deletion on termination",
    body: "On termination of the subscription, CombinedWork will delete or return the Controller's personal data within a reasonable period, except where retention is required by law or for legitimate accounting purposes, consistent with the Privacy Policy's retention terms.",
  },
  {
    title: "11. Requesting a signed copy",
    body: "This page sets out CombinedWork's standard DPA terms. If your organisation requires a counter-signed copy (for example as part of vendor due diligence before connecting Xero or HubSpot), email hello@combinedwork.com and we'll send one over.",
  },
];

export default function Dpa() {
  return (
    <div className="bg-white text-gray-900 min-h-screen font-sans">
      <nav className="border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-200">
              <LogoMark size={16} color="#fff" />
            </div>
            <span className="text-base font-bold text-gray-900 tracking-tight">Combined<span className="text-violet-600">Work</span></span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-2">Data Processing Agreement</h1>
        <p className="text-gray-400 text-sm mb-2">Last updated 19 September 2026</p>
        <p className="text-gray-500 text-sm mb-12 leading-relaxed">
          For agencies connecting HubSpot or Xero, this is our standard Data Processing Agreement under UK/EU GDPR,
          incorporated by reference into our Terms of Service. See also our <Link to="/privacy" className="text-violet-600 underline underline-offset-2">Privacy Policy</Link>.
        </p>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-semibold text-gray-900 text-base mb-2">{s.title}</h2>
              <p className="text-gray-500 text-sm leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
      </div>

      <footer className="border-t border-gray-100 py-8 px-6 text-center">
        <span className="text-xs text-gray-400">&copy; {new Date().getFullYear()} CombinedWork. All rights reserved.</span>
      </footer>
    </div>
  );
}
